import { Card } from '@ipfabric/ui-components/Card/Card';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { ListDivider } from '@ipfabric/ui-components/List/ListDivider/ListDivider';
import { createFormSnippetGroup } from '@ipfabric/ui-components/utils/formSnippets/FormSnippetGroup';
import { DEFAULT_APP_SETTING } from '@ipfabric/utils';
import { SearchableSection } from '../../../components/DiscoverySearch/SearchableSection';
import { duplicateIpList } from '../../../snippets/discovery/duplicateIpList';
import { ipScopeExclude } from '../../../snippets/discovery/ipScopeExclude';
import { ipScopeInclude } from '../../../snippets/discovery/ipScopeInclude';
import {
  convertAfterDateToFormValue,
  convertDaysLimitToFormValue,
  ipSeedHistory,
} from '../../../snippets/discovery/ipSeedHistory';
import { ipSeedList } from '../../../snippets/discovery/ipSeedList';
import { limitDiscoveryTasks } from '../../../snippets/discovery/limitDiscoveryTasks';
import { resolveNames } from '../../../snippets/discovery/resolveNames';
import { scanner } from '../../../snippets/discovery/scanner';
import { traceProtocol } from '../../../snippets/discovery/traceProtocol';
import {
  RFC_6890_SCOPES,
  traceScope,
} from '../../../snippets/discovery/traceScope';
import type { DiscoverySettingsGroupDisplayControl } from '../DiscoverySettings';

export const discoveryGroup = createFormSnippetGroup({
  snippets: {
    resolveNames,
    ipScopeInclude,
    ipSeedHistory,
    ipSeedList,
    ipScopeExclude,
    limitDiscoveryTasks,
    duplicateIpList,
    scanner,
    traceScope,
    traceProtocol,
  },
  render: ({ renderSnippet, form }) => (
    <div className='flex-1 flex flex-col gap'>
      <div className='text-default text-level-4 font-semibold font-heading'>
        Discovery
      </div>
      <Card>
        <SearchableSection
          id='discovery-ip-scope'
          className='p flex flex-col'
          highlightOn={['discovery']}
        >
          <div className='flex flex-row gap-xs'>
            <div className='text-default text-level-5 font-semibold font-heading'>
              IP scope
            </div>
            <Link
              renderAs='button'
              onClick={() => {
                form.setValue(
                  'ipScopeInclude',
                  {
                    ips: DEFAULT_APP_SETTING.networks.include,
                  },
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'ipScopeExclude',
                  {
                    ips: DEFAULT_APP_SETTING.networks.exclude,
                  },
                  { shouldDirty: true, shouldTouch: true },
                );
              }}
            >
              Reset to default values
            </Link>
          </div>
          <SearchableSection id='discovery-ip-scope-include'>
            <div className='my-xs'>{renderSnippet('ipScopeInclude')}</div>
          </SearchableSection>
          <ListDivider />
          <SearchableSection id='discovery-ip-scope-exclude'>
            <div className='my-xs'>{renderSnippet('ipScopeExclude')}</div>
          </SearchableSection>
        </SearchableSection>
      </Card>
      <Card>
        <SearchableSection
          id='discovery-ip-seeds'
          className='p flex flex-col'
          highlightOn={['discovery']}
        >
          <div className='flex flex-row gap-xs'>
            <div className='text-default text-level-5 font-semibold font-heading'>
              IP seeds
            </div>
            <Link
              renderAs='button'
              onClick={() => {
                form.setValue(
                  'ipSeedList',
                  {
                    ips: DEFAULT_APP_SETTING.discoverySeedIpList,
                  },
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'enabled',
                  DEFAULT_APP_SETTING.discoveryHistorySeeds.enabled,
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'afterDate',
                  convertAfterDateToFormValue(
                    DEFAULT_APP_SETTING.discoveryHistorySeeds.afterDate,
                  ),
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'daysLimit',
                  convertDaysLimitToFormValue(
                    DEFAULT_APP_SETTING.discoveryHistorySeeds.daysLimit,
                    DEFAULT_APP_SETTING.discoveryHistorySeeds.afterDate,
                  ),
                  { shouldDirty: true, shouldTouch: true },
                );
              }}
            >
              Reset to default values
            </Link>
          </div>
          <div className='text-default text-sm my-xs'>
            A discovery seed is a device from which IP Fabric begins the
            auto-discovery process. These IPs can be device management IP
            addresses or networks. Discovery seed settings do not include or
            exclude any networks from discovery.
          </div>
          <SearchableSection id='discovery-ip-seed-list'>
            <div className='my-xs'>{renderSnippet('ipSeedList')}</div>
          </SearchableSection>
          <SearchableSection id='discovery-ip-seed-history'>
            <div className='my-xs'>{renderSnippet('ipSeedHistory')}</div>
          </SearchableSection>
        </SearchableSection>
      </Card>
      <Card>
        <SearchableSection
          id='discovery-advanced'
          className='p flex flex-col'
          highlightOn={['discovery']}
        >
          <div className='flex flex-row gap-xs'>
            <div className='text-default text-level-5 font-semibold font-heading'>
              Advanced settings
            </div>
            <Link
              renderAs='button'
              onClick={() => {
                form.setValue(
                  'alreadyDiscovered',
                  DEFAULT_APP_SETTING.limitDiscoveryTasks.alreadyDiscovered,
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'sourceOfTasks',
                  DEFAULT_APP_SETTING.limitDiscoveryTasks.sourceOfTasks,
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'duplicateIpList',
                  {
                    ips: DEFAULT_APP_SETTING.networks.whitelist,
                  },
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'resolveNames',
                  DEFAULT_APP_SETTING.resolveNames.discoveryDevices,
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'scannerEnabled',
                  DEFAULT_APP_SETTING.scanner.enabled,
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'shortestMask',
                  String(DEFAULT_APP_SETTING.scanner.shortestMask ?? 20),
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'traceScope',
                  { ips: RFC_6890_SCOPES },
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'traceProtocol',
                  DEFAULT_APP_SETTING.trace.protocol,
                  { shouldDirty: true, shouldTouch: true },
                );
                form.setValue(
                  'tracePort',
                  String(DEFAULT_APP_SETTING.trace.port),
                  { shouldDirty: true, shouldTouch: true },
                );
              }}
            >
              Reset to default values
            </Link>
          </div>
          <SearchableSection id='discovery-limit-tasks'>
            <div className='my-xs'>{renderSnippet('limitDiscoveryTasks')}</div>
          </SearchableSection>
          <SearchableSection id='discovery-duplicate-ip-list'>
            <div className='my-xs'>{renderSnippet('duplicateIpList')}</div>
          </SearchableSection>
          <ListDivider />
          <SearchableSection id='discovery-trace-scope'>
            <div className='my-xs'>{renderSnippet('traceScope')}</div>
          </SearchableSection>
          <SearchableSection id='discovery-trace-protocol'>
            <div className='my-xs'>{renderSnippet('traceProtocol')}</div>
          </SearchableSection>
          <ListDivider />
          <SearchableSection id='discovery-resolve-names'>
            <div className='my-xs'>{renderSnippet('resolveNames')}</div>
          </SearchableSection>
          <SearchableSection id='discovery-scanner'>
            <div className='my-xs'>{renderSnippet('scanner')}</div>
          </SearchableSection>
        </SearchableSection>
      </Card>
    </div>
  ),
  show: (control: DiscoverySettingsGroupDisplayControl) => {
    control.setTab('discovery');
  },
});
